import {
    combineReducers,
    configureStore,
    ThunkAction,
    UnknownAction,
} from '@reduxjs/toolkit';
import { useDispatch } from 'react-redux';
import throttle from 'lodash.throttle';
import {
    Datadog,
    Rudderstack,
    rudderStackAnalytics,
} from '@tsl-frontend/tsl-utilities';

import appReducer from './app/app-slice';
import initialisationReducer from './initialisation/initialisation-slice';
import navReducer from './navigation/navigation-slice';
import programmeReducer from './programme/programme-slice';
import sessionReducer from './session/session-slice';

import { environment } from '@environments/environment';
import { RUDDERSTACK_TRACKED_EVENTS } from '@consts';

const loadState = () => {
    try {
        const serializedState = localStorage.getItem('state');
        if (serializedState === null) {
            return undefined;
        }
        return JSON.parse(serializedState);
    } catch (err) {
        return undefined;
    }
};

interface PersistedState {
    navigation: {
        open: boolean;
    };
}

const saveState = (state: PersistedState) => {
    try {
        const serializedState = JSON.stringify(state);
        localStorage.setItem('state', serializedState);
    } catch {
        // ignore write errors
    }
};

export const rootReducer = combineReducers({
    app: appReducer,
    navigation: navReducer,
    programme: programmeReducer,
    session: sessionReducer,
    init: initialisationReducer,
});

const trackedActionTypes: string[] = [];
export const store = configureStore({
    preloadedState: loadState(),
    reducer: rootReducer,
    devTools: process.env.NODE_ENV === 'development',
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware()
            .prepend(
                Datadog(trackedActionTypes, {
                    rumAppId: environment.ddRumAppId,
                    rumClientToken: environment.ddRumClientToken,
                    rumServiceName: environment.ddRumServiceName,
                    appEnv: environment.env,
                }),
            )
            .prepend(
                Rudderstack(
                    rudderStackAnalytics,
                    RUDDERSTACK_TRACKED_EVENTS,
                    [],
                ),
            ),
});

store.subscribe(
    throttle(() => {
        saveState({
            navigation: {
                open: store.getState().navigation.open,
            },
        });
    }, 1000),
); // limit to once per second

export type RootState = ReturnType<typeof rootReducer>;
export type AppDispatch = typeof store.dispatch;
export const useAppDispatch = () => useDispatch<AppDispatch>();

export type AppThunk<ReturnType = void> = ThunkAction<
    ReturnType,
    RootState,
    unknown,
    UnknownAction
>;
